import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_motherhood-rooted/artifacts/8xlacil8_6Y7A2202-142.jpeg";

const PATH_ITEMS = [
  {
    n: "01",
    text: (
      <>
        How to gently regulate your hormones and restore your cycle through food, nourishment, and the ancient wisdom of <em className="text-[#800606]">Traditional Chinese Medicine</em> and <em className="text-[#800606]">Ayurveda</em>.
      </>
    ),
  },
  {
    n: "02",
    text: (
      <>
        Personalized <em className="text-[#800606]">preconception nutrition</em> that prepares your body from the inside, supporting your egg quality, uterine lining, and conception, naturally or alongside IVF.
      </>
    ),
  },
  {
    n: "03",
    text: (
      <>
        How to effectively support PMOS (formerly known as PCOS), endometriosis, irregular or painful periods, low libido, and unexplained infertility by <em className="text-[#800606]">uncovering the pattern beneath your symptoms</em>, and what your body has been trying to tell you all along.
      </>
    ),
  },
  {
    n: "04",
    text: (
      <>
        Support for navigating <em className="text-[#800606]">IVF, IUI, and ART</em>, from retrieval prep through the two-week wait. Meals and rituals that calm your nervous system, support blood flow to your ovaries and uterus, and help you feel held each step of the way.
      </>
    ),
  },
  {
    n: "05",
    text: (
      <>
        The <em className="text-[#800606]">calm, clarity, and confidence</em> that comes from finally being heard, including support for healing after loss, strengthening male factor fertility, and learning to trust your body again.
      </>
    ),
  },
];

const INNER_THOUGHTS = [
  "I didn't expect trying to get pregnant would feel this confusing, this overwhelming.",
  "I didn't expect this to take as long as it has.",
  "I want to feel heard and supported by my doctors, not rushed past.",
  "I hope for good quality eggs, a healthy uterine lining, a body that's ready.",
  "I want to feel at home in my body again.",
  "I want to know which foods will actually help me.",
];

const FOR_YOU = [
  "You're planning to conceive in the next 1–2 years and want to prepare well in advance.",
  "You're coming off birth control and want to restore and nourish your cycle.",
  "You've been trying for months or years, with no clear answers.",
  "You're struggling with missing, irregular, or painful periods, low libido, or unexplained infertility.",
  "You're preparing for IVF or finding your way back to trying after loss.",
  "You want root-cause support, not another way to mask and manage the symptoms.",
];

const NOT_FOR_YOU = [
  "You want a guarantee, quick fix, or shortcut.",
  "You're not ready or willing to change how you eat or live.",
  "You want to be fixed without doing any of the work yourself.",
  "Root-cause education and support isn't what you're looking for right now.",
];

const WHAT_YOU_RECEIVE = [
  {
    h: "Six months, devoted entirely to you.",
    p: "A complete season of 1:1 doctor and nutritionist-led fertility coaching, designed to meet your body where it is.",
  },
  {
    h: "Biweekly 60-minute coaching calls.",
    p: "Just us. Unhurried. The space to be heard, asked the right questions, and walked beside.",
  },
  {
    h: "Voxer support, for real-time cycle, fertility, and nutrition questions.",
    p: "Available Monday–Friday, with replies within 24–48 hours, so you're never alone in the in-between.",
  },
  {
    h: "A personalized fertility protocol.",
    p: "Nutrition, teas, sleep, movement, stress, beauty, lifestyle, all built for your unique constitution and pattern, not a template.",
  },
  {
    h: "Recipes & seasonal grocery lists.",
    p: "Cycle-aware meals informed by holistic nutrition, Traditional Chinese Medicine, and Ayurvedic wisdom.",
  },
  {
    h: "First access to a growing library.",
    p: "Masterclasses and mini-courses, released over time as they're created. Yours to keep, well after our six months together.",
  },
  {
    h: "Holistic support for your partner's fertility, too.",
    p: "Because conception is a body of two. Male factor fertility is held with the same depth and care.",
  },
];

export default function Landing() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast("Please add your name and email before joining.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/waitlist", { ...form, primary_focus: "" });
      setSuccess(true);
      toast.success("You are on the list.");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Could not save your entry.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToWaitlist = (e) => {
    e?.preventDefault?.();
    document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const RedButton = ({ children, testid, onClick, type, disabled }) => (
    <button
      data-testid={testid}
      type={type || "button"}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-3 px-8 py-4 font-mono-cutive text-[12px] tracking-[0.2em] uppercase disabled:opacity-60"
      style={{ backgroundColor: "#800606", color: "#FFFCEF" }}
      onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#6a0505"; }}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#800606")}
    >
      {children} <span aria-hidden>→</span>
    </button>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFCEF", color: "#1A1A1A" }}>
      {/* TOP BAR */}
      <header className="w-full border-b border-[#E5D9B0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" data-testid="brand-wordmark" className="flex flex-col leading-none">
            <span className="font-display italic text-2xl md:text-3xl">Nourished</span>
            <span className="font-display text-2xl md:text-3xl tracking-tight" style={{ color: "#800606" }}>Fertility</span>
            <span className="meta-label mt-1">WITH DR. SYVANNA</span>
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28 grid grid-cols-12 gap-y-14 md:gap-x-12">
          <div className="col-span-12 md:col-span-7 nf-rise">
            <p className="meta-label" data-testid="hero-kicker">AN INVITATION</p>
            <h1 className="font-display mt-8 leading-[1.0] tracking-tight text-[36px] sm:text-[46px] md:text-[54px] lg:text-[62px]" data-testid="hero-title">
              For the woman doing <br className="hidden md:block" />
              everything <em style={{ color: "#800606" }}>to conceive</em>,
            </h1>
            <p className="font-display italic mt-6 text-[22px] md:text-[26px] lg:text-[28px] leading-[1.35] text-[#1A1A1A] max-w-xl">
              whose body has been asking for <span style={{ color: "#800606" }}>deep nourishing fertility support</span>, to finally get, and stay, pregnant.
            </p>

            <div className="editorial-rule mt-12" />

            <p className="mt-10 max-w-xl text-[19px] md:text-[21px] leading-[1.7] text-[#1A1A1A] nf-rise nf-delay-1">
              Receive the most effective practices and nutrition principles through <em>1:1 doctor and nutritionist-led fertility coaching</em> for six months, to prepare your body <em style={{ color: "#800606" }}>beautifully</em> for pregnancy and motherhood.
            </p>
            <p className="mt-5 max-w-xl text-[18px] md:text-[20px] leading-[1.7] text-[#1A1A1A] nf-rise nf-delay-1">
              Rooted in Traditional Chinese Medicine, Ayurvedic wisdom, and holistic fertility nutrition. We will nurture every aspect of your reproductive health.
            </p>
            <p className="mt-5 max-w-xl text-[18px] md:text-[20px] leading-[1.75] text-[#5A5A5A] nf-rise nf-delay-2">
              For the woman ready to regain confidence in her body, and her ability to conceive: this was made for you. Get early access before doors open.
            </p>

            <div className="mt-12 nf-rise nf-delay-3">
              <RedButton testid="hero-cta-button" onClick={scrollToWaitlist}>
                Yes, I'm Ready
              </RedButton>
            </div>
          </div>

          <aside className="col-span-12 md:col-span-5 nf-rise nf-delay-2 md:pt-[60px]">
            <figure>
              <div className="relative w-full overflow-hidden grain" style={{ aspectRatio: "4 / 5", backgroundColor: "#E5D9B0" }}>
                <img
                  data-testid="hero-image"
                  src={HERO_IMAGE}
                  alt="Dr. Syvanna"
                  loading="eager"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: "saturate(0.95) contrast(1.02)" }}
                />
              </div>
            </figure>
          </aside>
        </div>
      </section>

      {/* CREDO */}
      <section className="border-t border-[#E5D9B0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <p className="font-display italic text-[24px] sm:text-[28px] md:text-[34px] lg:text-[38px] leading-[1.25] text-[#1A1A1A]">
            A return to the slow, ancient ways of <span style={{ color: "#800606" }}>nourishing</span> our bodies through every season of the fertility journey.
          </p>
          <div className="editorial-rule mt-10 mx-auto" />
          <p className="meta-label mt-5">— DR. SYVANNA</p>
          <p className="font-display italic text-[16px] md:text-[17px] leading-[1.55] mt-2 text-[#5A5A5A] max-w-lg mx-auto">
            Doctor of Traditional Chinese Medicine &amp; Certified Holistic Nutritionist,<br />
            specializing in fertility and reproductive health.
          </p>
        </div>
      </section>

      {/* PATH AHEAD */}
      <section className="border-t border-[#E5D9B0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-10">
              <p className="meta-label">THE PATH AHEAD</p>
              <h2 className="font-display mt-6 text-[32px] sm:text-[42px] md:text-[52px] lg:text-[60px] leading-[1.05] tracking-tight whitespace-nowrap">
                Are <em style={{ color: "#800606" }}>you</em> ready to discover…
              </h2>
            </div>
          </div>

          <ol className="mt-16 md:mt-24" data-testid="path-list">
            {PATH_ITEMS.map((item) => (
              <li
                key={item.n}
                className="grid grid-cols-12 gap-6 md:gap-12 py-8 md:py-10 border-t border-[#1A1A1A]/15 last:border-b"
                data-testid={`path-item-${item.n}`}
              >
                <div className="col-span-2 md:col-span-1 font-mono-cutive text-[18px] md:text-[22px] text-[#1A1A1A] pt-1">
                  {item.n}.
                </div>
                <div className="col-span-10 md:col-span-11 font-display text-[20px] md:text-[26px] lg:text-[28px] leading-[1.45] text-[#1A1A1A]">
                  {item.text}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="border-t border-[#E5D9B0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <p className="meta-label">WHO THIS IS FOR</p>
              <h2 className="font-display mt-6 text-[36px] md:text-[52px] leading-[1.0] tracking-tight">
                If you have <em style={{ color: "#800606" }}>ever felt…</em>
              </h2>
              <div className="editorial-rule mt-10" />
              <p className="meta-label mt-5">FOR THE WOMAN BEING ASKED TO GO DEEPER</p>
            </div>

            <div className="col-span-12 md:col-span-7 space-y-6 text-[20px] md:text-[21px] leading-[1.75] text-[#1A1A1A]">
              <p>You're exhausted by the rollercoaster. Doing everything right. Overthinking every symptom, every tracking app, every cycle phase. Disconnected from your body. Stuck in survival mode. Nothing has worked, <em style={{ color: "#800606" }}>yet</em>.</p>
              <p>You're doing all the testing, the tracking, and ticking all the boxes. But no one has asked: <em style={{ color: "#800606" }}>how are you being nourished?</em></p>
              <p>You've recently gotten married, spent a decade or more on birth control, and you're ready to restore your cycle for a healthy conception and a thriving pregnancy.</p>
              <p>You've been trying to conceive for months, or years. You want deep support for your egg quality, your uterine lining, and your odds of getting and staying pregnant.</p>
            </div>
          </div>

          {/* MAYBE YOU'VE THOUGHT */}
          <div className="mt-20 md:mt-28">
            <p className="meta-label">MAYBE YOU'VE THOUGHT…</p>
            <div className="mt-10 grid grid-cols-12 gap-6 md:gap-10">
              {INNER_THOUGHTS.map((t, i) => (
                <blockquote
                  key={i}
                  className="col-span-12 md:col-span-6 border-l-2 border-[#800606] pl-6 py-2 font-display italic text-[20px] md:text-[22px] leading-[1.5] text-[#1A1A1A]"
                  data-testid={`thought-${i}`}
                >
                  "{t}"
                </blockquote>
              ))}
            </div>
          </div>

          {/* THIS IS FOR YOU / NOT FOR YOU */}
          <div className="mt-20 md:mt-28 grid grid-cols-12 gap-12">
            <div className="col-span-12 md:col-span-6">
              <p className="meta-label">THIS IS FOR YOU, IF</p>
              <ul className="mt-8 space-y-5" data-testid="for-you-list">
                {FOR_YOU.map((item, i) => (
                  <li key={i} className="grid grid-cols-12 gap-4 text-[17px] md:text-[18px] leading-[1.65] text-[#1A1A1A]">
                    <span className="col-span-1 font-mono-cutive text-[14px] text-[#800606] pt-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="col-span-11">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-12 md:col-span-6">
              <p className="meta-label">NOT FOR YOU, IF</p>
              <ul className="mt-8 space-y-5" data-testid="not-for-you-list">
                {NOT_FOR_YOU.map((item, i) => (
                  <li key={i} className="grid grid-cols-12 gap-4 text-[17px] md:text-[18px] leading-[1.65] text-[#5A5A5A]">
                    <span className="col-span-1 font-mono-cutive text-[14px] pt-1">
                      ×
                    </span>
                    <span className="col-span-11">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL RECEIVE */}
      <section className="border-t border-[#E5D9B0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-7">
              <p className="meta-label">WHAT YOU'LL RECEIVE</p>
              <h2 className="font-display mt-6 text-[36px] md:text-[60px] leading-[1.0] tracking-tight">
                Six months, <em style={{ color: "#800606" }}>devoted</em> <br className="hidden md:block" /> entirely to you.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5">
              <p className="font-display italic text-[19px] md:text-[21px] leading-[1.55] text-[#5A5A5A]">
                The shape of our season together — unhurried, personal, and built around your body's particular language.
              </p>
            </div>
          </div>

          <ol className="mt-16 md:mt-24" data-testid="receive-list">
            {WHAT_YOU_RECEIVE.map((item, i) => (
              <li
                key={i}
                className="grid grid-cols-12 gap-6 md:gap-12 py-8 md:py-10 border-t border-[#1A1A1A]/15 last:border-b"
              >
                <div className="col-span-2 md:col-span-1 font-mono-cutive text-[18px] md:text-[22px] text-[#1A1A1A] pt-2">
                  {String(i + 1).padStart(2, "0")}.
                </div>
                <div className="col-span-10 md:col-span-7">
                  <h3 className="font-display text-[22px] md:text-[28px] leading-[1.2]">{item.h}</h3>
                </div>
                <div className="col-span-12 md:col-span-4 font-display italic text-[18px] md:text-[19px] leading-[1.6] text-[#5A5A5A]">
                  {item.p}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHY THIS WORK */}
      <section className="border-t border-[#E5D9B0]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <p className="meta-label text-center">WHY THIS WORK</p>
          <h2 className="font-display text-center mt-6 text-[36px] md:text-[60px] leading-[1.0] tracking-tight">
            Your body is asking <br className="hidden md:block" />
            for <em style={{ color: "#800606" }}>fertile soil.</em>
          </h2>
          <div className="editorial-rule mt-10 mx-auto" />

          <div className="mt-14 md:mt-20 space-y-7 text-[18px] md:text-[20px] leading-[1.75] text-[#1A1A1A] max-w-3xl mx-auto">
            <p>I work with women who have tried everything, and want to go to the root. Women ready to <em style={{ color: "#800606" }}>understand and nurture</em> their fertility, not just manage another symptom.</p>
            <p>1:1 doctor-led fertility coaching, rooted in Traditional Chinese Medicine, holistic reproductive nutrition, Ayurvedic wisdom, and protocols built entirely around <em>you</em>.</p>
            <p>Instead of Googling symptoms at 2 a.m., you'll finally understand what your body has been trying to tell you. No more feeling dismissed, confused, or alone.</p>
            <p>One-on-one time to be heard. Support to advocate for yourself. Insight into what your body has been asking for, all along. Time-honored fertility and reproductive health rituals and practices, chosen specifically for <em>you</em>.</p>
            <p className="font-display italic text-[22px] md:text-[26px] leading-[1.5] text-[#1A1A1A]">
              Your body is asking for fertile soil. A strong foundation, built before conception, because your wellness now shapes the health of your future child, and the generations after.
            </p>
            <p>Because you deserve a path to a <em style={{ color: "#800606" }}>healthy conception</em>, walked beside someone who sees the whole of you.</p>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section id="waitlist" className="border-t border-[#E5D9B0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-12 gap-12">
          <div className="col-span-12 md:col-span-5">
            <p className="meta-label">JOIN THE WAITLIST</p>
            <h2 className="font-display mt-6 text-[40px] md:text-[56px] leading-[1.0] tracking-tight">
              Secure <em style={{ color: "#800606" }}>your</em> place.
            </h2>
            <div className="editorial-rule mt-10" />
            <figure className="mt-14">
              <blockquote className="font-display italic text-[28px] md:text-[34px] leading-[1.25] text-[#1A1A1A]">
                "Nourish the soil <br />
                <span style={{ color: "#800606" }}>before</span> you plant the seed."
              </blockquote>
              <figcaption className="meta-label mt-6">A TRADITIONAL CHINESE MEDICINE FERTILITY PROVERB</figcaption>
            </figure>
          </div>

          <div className="col-span-12 md:col-span-7">
            {success ? (
              <div className="border border-[#E5D9B0] p-10 md:p-14" style={{ backgroundColor: "#FFFCEF" }} data-testid="waitlist-success">
                <p className="meta-label">CONFIRMED</p>
                <h3 className="font-display mt-6 text-[36px] md:text-[44px] leading-[1.05]">
                  You are <em style={{ color: "#800606" }}>on the list.</em>
                </h3>
                <p className="mt-8 text-[18px] leading-[1.7] text-[#1A1A1A] max-w-md">
                  When the doors of Nourished Fertility open, you will be the first to know.
                </p>
                <p className="mt-6 font-display italic text-[18px] text-[#5A5A5A]">
                  With love, <br />Dr. Syvanna
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-10" data-testid="waitlist-form">
                <div>
                  <label htmlFor="name" className="meta-label">Your Name</label>
                  <input
                    id="name"
                    data-testid="waitlist-name-input"
                    type="text"
                    autoComplete="name"
                    placeholder="E.g., Clara Thorne"
                    value={form.name}
                    onChange={onChange("name")}
                    disabled={submitting}
                    className="editorial-input mt-2"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="meta-label">Email Address</label>
                  <input
                    id="email"
                    data-testid="waitlist-email-input"
                    type="email"
                    autoComplete="email"
                    placeholder="E.g., clara@example.com"
                    value={form.email}
                    onChange={onChange("email")}
                    disabled={submitting}
                    className="editorial-input mt-2"
                  />
                </div>

                <RedButton testid="waitlist-submit-button" type="submit" disabled={submitting}>
                  {submitting ? "Sending…" : "Yes, I'm Ready"}
                </RedButton>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5D9B0]" style={{ backgroundColor: "#FFFCEF" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-6">
            <div className="font-display leading-[0.95]">
              <div className="italic text-[40px] md:text-[56px]">Nourished</div>
              <div className="text-[40px] md:text-[56px] tracking-tight" style={{ color: "#800606" }}>Fertility</div>
            </div>
            <p className="meta-label mt-4">WITH DR. SYVANNA</p>
          </div>
          <div className="col-span-12 md:col-span-6 md:text-right">
            <p className="font-display italic text-[22px] md:text-[26px]">come home to your body</p>
            <div className="md:ml-auto editorial-rule mt-6" />
            <p className="meta-label mt-4">&copy; 2026 NOURISHED FERTILITY. ALL RIGHTS RESERVED.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
